/**
 * Phase 6P — Shared Framer Motion variants.
 * Single source of truth for all animations. Subtle, branded, fast.
 * All durations 200–400ms, ease-out. Honor prefers-reduced-motion via CSS.
 */

// Page-level transition (used by AppShell <AnimatePresence>)
export const pageTransition = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
};

// Single-element fade + lift
export const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
};

// Children stagger (use on parent <motion.div>)
export const staggerChildren = {
  initial: {},
  animate: { transition: { staggerChildren: 0.06, delayChildren: 0.04 } },
};

// Item used inside a staggerChildren parent
export const staggerItem = {
  initial: { opacity: 0, y: 14, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
};

// Card hover lift
export const cardHover = {
  whileHover: { y: -4, transition: { duration: 0.18, ease: "easeOut" } },
  whileTap: { y: -1, transition: { duration: 0.1 } },
};

// Button press
export const buttonPress = {
  whileHover: { scale: 1.02 },
  whileTap: { scale: 0.97 },
  transition: { type: "spring", stiffness: 400, damping: 20 },
};

// Modal/drawer enter
export const modalEnter = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.96 },
  transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] },
};

// Breathing pulse (GST FAB)
export const breathing = {
  animate: { scale: [1, 1.04, 1], opacity: [0.92, 1, 0.92] },
  transition: { duration: 3.4, repeat: Infinity, ease: "easeInOut" },
};

/* ============================================================
 * Phase 17.1 aliases -- unified motion vocabulary.
 * Roadmap A.1 named tokens PAGE_FADE / CARD_LIFT / LIST_STAGGER /
 * MODAL_SPRING. Rather than duplicate the Phase 6P tokens above, we
 * expose the same values under the new names so both call-sites work
 * during the migration.
 * ============================================================ */
export const PAGE_FADE = {
  hidden: { opacity: 0, y: 8 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
  exit:   { opacity: 0, y: -6, transition: { duration: 0.15, ease: "easeIn" } },
};

export const CARD_LIFT = {
  rest:  { y: 0, boxShadow: "0 8px 24px -18px rgba(0,0,0,0.4)" },
  hover: {
    y: -3,
    boxShadow: "0 20px 40px -20px rgba(0,0,0,0.55)",
    transition: { duration: 0.15, ease: "easeOut" },
  },
};

export const LIST_STAGGER = {
  hidden: { opacity: 1 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.03, delayChildren: 0.02 },
  },
};

export const LIST_ITEM = {
  hidden: { opacity: 0, x: -6 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.18, ease: "easeOut" } },
};

export const MODAL_SPRING = {
  initial: { opacity: 0, scale: 0.96, y: 12 },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 320, damping: 26 },
  },
  exit: { opacity: 0, scale: 0.97, y: 8, transition: { duration: 0.14 } },
};

// Sugar for the common case: an outer <motion.div> that fades in on mount.
export const PAGE_ENTER_PROPS = {
  variants: PAGE_FADE,
  initial: "hidden",
  animate: "show",
  exit: "exit",
};
